import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStudioMe, useStudioOverview } from '@/api/studio';
import type { StudioAttention } from '@/api/types';
import { PageHeader } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { groupByClient, isUrgent } from '@/lib/attention';
import { formatDate, formatMoney, plural } from '@/lib/format';
import { answered } from '@/lib/initiatives';
import { problemLabel } from '@/lib/status';
import { IntegrationsAlert } from './StudioAdmin';

const DASH = '—';

type Row = { title: string; hint: string; to: string; urgency: 'high' | 'mid' };

/** One row; the client is the group's heading, so the hint does not repeat it. */
function describe(a: StudioAttention): Row {
  switch (a.kind) {
    case 'reply':
      return {
        title: a.thread.lastMessage,
        hint: `«${a.thread.title}», ждёт ${a.waitingHours} ${plural(a.waitingHours, 'час', 'часа', 'часов')}`,
        to: `/studio/projects/${a.thread.projectId}/talk/${a.thread.id}`,
        urgency: isUrgent(a) ? 'high' : 'mid',
      };
    case 'overdue':
      return { title: `Просрочен счёт № ${a.invoice.number} на ${formatMoney(a.invoice.total)}`, hint: 'Напомнить или позвонить', to: `/studio/money`, urgency: 'high' };
    case 'act_missing':
      if (a.invoice.actStatus === 'draft')
        return {
          title: `Оплачен счёт № ${a.invoice.number}: черновик акта № ${a.invoice.actNumber} ждёт проверки`,
          hint: 'Проверьте строки и отправьте клиенту на подпись — в карточке клиента, раздел «Акты»',
          to: a.invoice.clientId ? `/studio/clients/${a.invoice.clientId}` : `/studio/money`,
          urgency: 'high',
        };
      return {
        title: `У оплаченного счёта № ${a.invoice.number} нет акта${a.invoice.actId ? ' (PDF не сформирован)' : ''}`,
        hint: 'Акт нужен клиенту для подписи; «Подготовить черновик» или «Сформировать заново» — на странице счетов',
        to: `/studio/money`,
        urgency: 'high',
      };
    case 'retainer_due':
      return {
        title: `Пора выставить счёт по регулярным платежам «${a.project.title}» на ${formatMoney(a.price)}`,
        hint: `Срок ${formatDate(a.dueAt)}; автоматический выпуск выключен — выставите счёт в карточке клиента`,
        to: `/studio/clients/${a.clientId}`,
        urgency: 'mid',
      };
    case 'payment_issue':
      return {
        title: `Проблема с оплатой счёта № ${a.checkout.invoiceNumber ?? a.invoiceId}: ${problemLabel[a.checkout.problem ?? 'record_failed']}`,
        hint: 'Деньги могли дойти до банка, а в портале не отражены',
        to: `/studio/money`,
        urgency: 'high',
      };
    case 'stale_client':
      return {
        title: `${a.task.title}: клиент молчит ${a.days} ${plural(a.days, 'день', 'дня', 'дней')}`,
        hint: a.task.needsClient?.note ?? '',
        to: `/studio/projects/${a.task.projectId}/tasks`,
        urgency: a.days > 3 ? 'high' : 'mid',
      };
    case 'now_outdated':
      return {
        title: `«Сейчас» не обновлялось ${a.days} ${plural(a.days, 'день', 'дня', 'дней')}`,
        hint: a.project.title,
        to: `/studio/projects/${a.project.id}`,
        urgency: 'mid',
      };
    case 'rhythm':
      return {
        title: `Регулярная работа «${a.rhythm.title}» по проекту «${a.project.title}» просрочена с ${formatDate(a.rhythm.next)}`,
        hint: `${a.days} ${plural(a.days, 'день', 'дня', 'дней')} без движения`,
        to: `/studio/projects/${a.project.id}`,
        urgency: 'mid',
      };
    case 'change':
      return { title: `${answered(a.change) ? 'Клиент ответил' : a.change.urgency === 'urgent' ? 'Срочная идея клиента' : 'Идея клиента'}: ${a.change.title}`, hint: answered(a.change) ? 'Ответ на вопрос студии, решите дальше' : 'Взять, оценить, уточнить, отложить или отказать', to: `/studio/projects/${a.change.projectId}/changes`, urgency: 'high' };
    case 'change_unanswered':
      return {
        title: `Оценка без ответа ${a.days} ${plural(a.days, 'день', 'дня', 'дней')}: ${a.change.title}`,
        hint: 'Клиент не ответил на оценку — напомнить или отклонить запрос',
        to: `/studio/projects/${a.change.projectId}/changes`,
        urgency: 'mid',
      };
    case 'proposal_silent':
      return { title: `Тишина по КП «${a.proposal.title}»`, hint: `${a.days} дн. без ответа — дожать или закрыть как отказ`, to: `/studio/clients/${a.proposal.clientId}`, urgency: 'mid' };
    case 'secret':
      return { title: `Доступ для вас: ${a.secret.title}`, hint: `от ${a.secret.from.name}, откроется один раз`, to: a.clientId ? `/studio/clients/${a.clientId}` : '/studio/clients', urgency: 'mid' };
    case 'onboarding':
      return {
        title: `Запуск: ${a.count} ${plural(a.count, 'пункт', 'пункта', 'пунктов')}, клиент ещё не дал`,
        hint: `${a.project.title}, начать с «${a.item.title}»`,
        to: `/studio/projects/${a.project.id}/start`,
        urgency: 'mid',
      };
    case 'payer_incomplete':
      return {
        title: `Реквизиты для счетов: у ${a.payer.legalName || 'плательщика'} не указан ${a.payer.missing.join(', ')}`,
        hint: `Клиента попросили дополнить; счета выставляются и без этого. ${a.payer.projects.map((p) => `«${p.title}»`).join(', ')}`,
        to: `/studio/clients/${a.clientId}`,
        urgency: 'mid',
      };
    case 'webhook_disabled':
      return {
        title: `Вебхук ${a.endpoint.name ? `«${a.endpoint.name}»` : a.endpoint.url} выключен: 3 дня без успешной доставки`,
        hint: `Клиенту ушло уведомление${a.endpoint.disabledAt ? `, выключен ${formatDate(a.endpoint.disabledAt)}` : ''}. Журнал доставок — в карточке клиента, «Интеграции»`,
        to: `/studio/clients/${a.clientId}`,
        urgency: 'mid',
      };
    default: {
      // A kind this build does not know yet (a newer bff): a plain row, never a broken screen.
      const kind = (a as { kind?: unknown }).kind;
      return { title: `Требует внимания${typeof kind === 'string' ? `: ${kind}` : ''}`, hint: 'Обновите страницу, чтобы увидеть подробности', to: '/studio', urgency: 'mid' };
    }
  }
}

/** The attention list: a collapsible group per client, the client with the most urgent item on top. */
export function AttentionList({ items }: { items: StudioAttention[] }) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (client: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(client)) next.add(client);
      return next;
    });
  return (
    <div className="space-y-6">
      {groupByClient(items).map((g) => {
        const open = !collapsed.has(g.client);
        const label = g.client || 'Без клиента';
        return (
          <section key={g.client} aria-label={label}>
            <button type="button" aria-expanded={open} onClick={() => toggle(g.client)} className="flex w-full items-center gap-2 border-b border-line pb-2 text-left">
              {open ? <ChevronDown className="size-4 text-faint" /> : <ChevronRight className="size-4 text-faint" />}
              <span className="font-semibold">{label}</span>
              <span className="num text-sm text-muted">{g.items.length}</span>
              {g.urgent > 0 && <span className="num text-xs font-semibold text-overdue">срочных: {g.urgent}</span>}
            </button>
            {open && (
              <ul className="divide-y divide-line">
                {g.items.map((a, i) => {
                  const d = describe(a);
                  return (
                    <li key={i}>
                      <Link to={d.to} className="group flex items-center gap-4 py-3 hover:bg-panel">
                        <span className={d.urgency === 'high' ? 'size-2 shrink-0 rounded-full bg-overdue' : 'size-2 shrink-0 rounded-full bg-waiting'} aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{d.title}</span>
                          <span className="block truncate text-sm text-muted">{d.hint}</span>
                        </span>
                        <ChevronRight className="size-4 text-faint" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function StudioHome() {
  const overviewQ = useStudioOverview();
  const { data } = overviewQ;
  const me = useStudioMe();
  if (!data) return <QueryPending query={overviewQ} />;

  const unavailable = data.unavailable ?? [];
  const down = [unavailable.includes('money') ? 'Счета' : null, unavailable.includes('tasks') ? 'задачи' : null].filter((x): x is string => !!x);
  const banner = down.length ? `${down.join(' и ').replace(/^./, (c) => c.toUpperCase())} временно недоступны` : null;

  return (
    <>
      <PageHeader title="Внимание" description="Что сейчас держит клиентов: сначала просрочки и платежи, потом неотвеченные сообщения, зависшие согласования и устаревший статус проектов." />
      {banner && (
        <p role="alert" className="mb-4 rounded-lg p-3 text-sm shadow-hair">
          {banner}: показатели ниже помечены «—», это не ноль.
        </p>
      )}
      <dl className="mb-10 grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-line shadow-hair md:grid-cols-4">
        {[
          ['Оплачено в этом месяце', data.revenueMonth ? formatMoney(data.revenueMonth) : DASH],
          ['Ждём оплаты', data.receivable ? formatMoney(data.receivable) : DASH],
          ['Активных клиентов', String(data.activeClients)],
          ['Открытых задач', data.openTasks === null ? DASH : String(data.openTasks)],
        ].map(([k, v]) => (
          <div key={k} className="bg-canvas p-4">
            <dt className="text-sm text-muted">{k}</dt>
            <dd className="num mt-1 text-xl font-semibold tracking-tight">{v}</dd>
          </div>
        ))}
      </dl>

      {me?.studioRole === 'owner' && <IntegrationsAlert />}

      {data.attention.length === 0 ? <p className="text-muted">Всё под контролем: клиенты не ждут студию.</p> : <AttentionList items={data.attention} />}
    </>
  );
}
